import type { TileContentPackage } from '@/plugins/tiles/contracts'
import { tileCostBytes, tileMemoryCosts } from '@/plugins/tiles/runtime/TileCache'

interface Retirement {
  readonly fallback: () => void
  readonly steps: Iterator<() => void>
  readonly bytes: number
  readonly cpuBytes: number
  readonly gpuBytes: number
}

/** 独立于渲染循环推进；整个包物理清理完毕前保守保留其费用。 */
export class TileRetirementQueue {
  private readonly _queue: Retirement[] = []
  private _timer?: ReturnType<typeof setTimeout>
  private _closed = false

  constructor(
    private readonly _onError: (error: unknown) => void,
    private readonly _onChange: () => void
  ) {}

  enqueue(content: TileContentPackage): void {
    const costs = content.costs
    let steps: Iterator<() => void>
    try {
      steps = content.createRetirement?.() ?? [() => content.dispose()][Symbol.iterator]()
    } catch (error) {
      this.report(error)
      steps = [() => content.dispose()][Symbol.iterator]()
    }
    this._queue.push({
      steps,
      fallback: () => content.dispose(),
      bytes: tileCostBytes(costs),
      ...tileMemoryCosts(costs),
    })
    // 限制等待拥有者数量；饱和只同步清理最老项，不丢弃唯一资源副本。
    if (this._closed) this.drain()
    else {
      while (this._queue.length > 256) this.flush(1, Infinity)
      this.schedule()
    }
  }

  getStatus() {
    return Object.freeze(
      this._queue.reduce(
        (sum, item) => ({
          count: sum.count + 1,
          bytes: sum.bytes + item.bytes,
          cpuBytes: sum.cpuBytes + item.cpuBytes,
          gpuBytes: sum.gpuBytes + item.gpuBytes,
        }),
        { count: 0, bytes: 0, cpuBytes: 0, gpuBytes: 0 }
      )
    )
  }

  close(): void {
    this._closed = true
    this.drain()
  }

  drain(): void {
    clearTimeout(this._timer)
    this._timer = undefined
    this.flush(Infinity, Infinity)
  }

  private schedule(): void {
    if (this._timer !== undefined || !this._queue.length) return
    this._timer = setTimeout(() => {
      this._timer = undefined
      this.flush(16, 2)
      this.schedule()
    }, 0)
  }

  private flush(maxSteps: number, maxMs: number): void {
    const started = performance.now()
    let changed = false
    for (
      let count = 0;
      this._queue.length && count < maxSteps && performance.now() - started <= maxMs;
      count++
    ) {
      const item = this._queue[0]
      try {
        const next = item.steps.next()
        if (next.done) {
          this._queue.shift()
          changed = true
        } else {
          try {
            next.value()
          } catch (error) {
            this.report(error)
          }
        }
      } catch (error) {
        // 协议迭代器故障应由包的 dispose 兜底；内置迭代器不会抛出资源清理错误。
        this._queue.shift()
        changed = true
        this.report(error)
        try {
          item.fallback()
        } catch (cleanupError) {
          this.report(cleanupError)
        }
      }
    }
    if (changed) {
      try {
        this._onChange()
      } catch (error) {
        this.report(error)
      }
    }
  }

  private report(error: unknown): void {
    try {
      this._onError(error)
    } catch {
      /* 保证其余项继续回收。 */
    }
  }
}
